import { cloneDeep } from 'lodash'
import i18n from '@/i18n'

import AbstractFilter from '@/classes/filters/AbstractFilter'

export default class DateRangeFilter extends AbstractFilter {
  /**
   * Create new date range filter
   *
   * @param {String} key
   * @param {String} property
   * @param {Array} [value] array of dates [startDate, endDate]
   */
  constructor (key, property, value) {
    super(key, null, property, value)

    let [startDate = null, endDate = null] = value || []

    if (startDate && !(startDate instanceof Date)) {
      startDate = new Date(startDate)
    }

    if (endDate && !(endDate instanceof Date)) {
      endDate = new Date(endDate)
    }

    this._value = { startDate, endDate }
    this._temporaryValue = { startDate, endDate }
  }

  /**
   * Get start date
   *
   * @return {Date}
   */
  get startDate () {
    return this.hasValue() ? this.value.startDate : null
  }

  /**
   * Set the start date of this filter
   *
   * @param {Date} value
   */
  set startDate (value) {
    this._value.startDate = value
    this._temporaryValue.startDate = value
  }

  /**
   * Get end date
   *
   * @return {Date}
   */
  get endDate () {
    return this.hasValue() ? this.value.endDate : null
  }

  /**
   * Set the end date of this filter
   *
   * @param {Date} value
   */
  set endDate (value) {
    this._value.endDate = value
    this._temporaryValue.endDate = value
  }

  /**
   * Get display value of this filter.
   *
   * @return {String}
   */
  get displayValue () {
    if (this.hasValue()) {
      return (i18n.d(this.value.startDate, 'short') === i18n.d(this.value.endDate, 'short'))
        ? `${i18n.d(this.value.startDate, 'short')}`
        : `${i18n.d(this.value.startDate, 'short')} - ${i18n.d(this.value.endDate, 'short')}`
    }

    return ''
  }

  /**
   * Get value of this filter
   *
   * @return {String}
   */
  toString () {
    return this.hasValue() ? `[gte]${this.value.startDate.toISOString()}[and][lte]${this.value.endDate.toISOString()}` : ''
  }

  /**
   * Does this filter have a value?
   *
   * @return {boolean}
   */
  hasValue () {
    return this.value !== null && typeof this.value !== 'undefined' && !!(this.value.startDate || this.value.endDate)
  }

  /**
   * Reset filter
   */
  reset () {
    this._value = { startDate: null, endDate: null }
    this._temporaryValue = { startDate: null, endDate: null }
  }

  /**
   * Cancel filter
   */
  cancel () {
    this._temporaryValue = this._value ? cloneDeep(this._value) : { startDate: null, endDate: null }
  }
}
